import type { UserSettingsDto } from "@eb/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, Card, ErrorBanner, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../state/auth";

/** The 16 Bundesländer, keyed by the two-letter code used in the question catalogue. */
const BUNDESLAENDER: { code: string; name: string }[] = [
    { code: "BW", name: "Baden-Württemberg 巴登-符腾堡" },
    { code: "BY", name: "Bayern 巴伐利亚" },
    { code: "BE", name: "Berlin 柏林" },
    { code: "BB", name: "Brandenburg 勃兰登堡" },
    { code: "HB", name: "Bremen 不来梅" },
    { code: "HH", name: "Hamburg 汉堡" },
    { code: "HE", name: "Hessen 黑森" },
    { code: "MV", name: "Mecklenburg-Vorpommern 梅前州" },
    { code: "NI", name: "Niedersachsen 下萨克森" },
    { code: "NW", name: "Nordrhein-Westfalen 北威州" },
    { code: "RP", name: "Rheinland-Pfalz 莱法州" },
    { code: "SL", name: "Saarland 萨尔" },
    { code: "SN", name: "Sachsen 萨克森" },
    { code: "ST", name: "Sachsen-Anhalt 萨安州" },
    { code: "SH", name: "Schleswig-Holstein 石荷州" },
    { code: "TH", name: "Thüringen 图林根" }
];

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 py-3.5">
            <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{label}</p>
                {hint ? <p className="mt-0.5 text-xs text-ink-soft">{hint}</p> : null}
            </div>
            <div className="shrink-0">{children}</div>
        </div>
    );
}

function Toggle({
    checked,
    onChange,
    label
}: {
    checked: boolean;
    onChange: (value: boolean) => void;
    label: string;
}) {
    return (
        <button
            role="switch"
            aria-checked={checked}
            aria-label={label}
            onClick={() => onChange(!checked)}
            className={`relative h-6 w-11 rounded-full transition-colors ${
                checked ? "bg-brand" : "bg-line"
            }`}
        >
            <span
                className={`absolute top-0.5 size-5 rounded-full bg-white transition-[left] ${
                    checked ? "left-[1.375rem]" : "left-0.5"
                }`}
            />
        </button>
    );
}

export function SettingsPage() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });

    const [draft, setDraft] = useState<UserSettingsDto | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (settingsQuery.data) setDraft(settingsQuery.data.settings);
    }, [settingsQuery.data]);

    const saveMutation = useMutation({
        mutationFn: (changes: Partial<UserSettingsDto>) => api.settings.update(changes),
        onSuccess: (data) => {
            queryClient.setQueryData(["settings"], data);
            // Scheduling parameters changed, so any cached queue is stale.
            void queryClient.invalidateQueries({ queryKey: ["queue"] });
            void queryClient.invalidateQueries({ queryKey: ["stats"] });
            void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        }
    });

    if (settingsQuery.isLoading || !draft) return <Spinner />;
    if (settingsQuery.isError) {
        return <ErrorBanner message="加载设置失败" onRetry={() => void settingsQuery.refetch()} />;
    }

    const update = <K extends keyof UserSettingsDto>(key: K, value: UserSettingsDto[K]) =>
        setDraft((previous) => (previous ? { ...previous, [key]: value } : previous));

    const dirty =
        settingsQuery.data !== undefined &&
        JSON.stringify(draft) !== JSON.stringify(settingsQuery.data.settings);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-ink">设置</h1>
                <p className="mt-1 text-sm text-ink-soft">{user?.email}</p>
            </div>

            <Card className="divide-y divide-line px-5">
                <Row
                    label="目标记忆保持率"
                    hint="调高会更频繁地复习、记得更牢；调低则复习量更少。推荐 90%。"
                >
                    <div className="flex items-center gap-3">
                        <input
                            type="range"
                            min={0.7}
                            max={0.98}
                            step={0.01}
                            value={draft.desiredRetention}
                            onChange={(event) => update("desiredRetention", Number(event.target.value))}
                            className="w-40 accent-brand"
                            aria-label="目标记忆保持率"
                        />
                        <span className="w-12 text-sm text-ink tabular-nums">
                            {Math.round(draft.desiredRetention * 100)}%
                        </span>
                    </div>
                </Row>

                <Row label="每天新题上限" hint="每天最多引入多少道没学过的题">
                    <input
                        type="number"
                        min={0}
                        max={310}
                        value={draft.dailyNewLimit}
                        onChange={(event) => update("dailyNewLimit", Number(event.target.value))}
                        className="w-24 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink tabular-nums"
                        aria-label="每天新题上限"
                    />
                </Row>

                <Row label="每天复习上限" hint="到期卡片一天最多做多少张">
                    <input
                        type="number"
                        min={0}
                        max={2000}
                        value={draft.dailyReviewLimit}
                        onChange={(event) => update("dailyReviewLimit", Number(event.target.value))}
                        className="w-24 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink tabular-nums"
                        aria-label="每天复习上限"
                    />
                </Row>

                <Row label="最长复习间隔" hint="单张卡片两次复习之间最多隔多少天">
                    <input
                        type="number"
                        min={1}
                        max={36500}
                        value={draft.maximumIntervalDays}
                        onChange={(event) => update("maximumIntervalDays", Number(event.target.value))}
                        className="w-24 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink tabular-nums"
                        aria-label="最长复习间隔天数"
                    />
                </Row>
            </Card>

            <Card className="divide-y divide-line px-5">
                <Row label="所在联邦州" hint="模拟考的 3 道州题从这个州的题库抽取">
                    <select
                        value={draft.bundesland}
                        onChange={(event) => update("bundesland", event.target.value)}
                        className="rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink"
                        aria-label="所在联邦州"
                    >
                        {BUNDESLAENDER.map((land) => (
                            <option key={land.code} value={land.code}>
                                {land.name}
                            </option>
                        ))}
                    </select>
                </Row>

                <Row label="显示题目和选项的中文翻译" hint="德语原文保持不变，译文显示在下方。模拟考始终只显示德语。">
                    <Toggle
                        checked={draft.showQuestionTranslation}
                        onChange={(value) => update("showQuestionTranslation", value)}
                        label="显示题目和选项的中文翻译"
                    />
                </Row>

                <Row label="显示中文解析">
                    <Toggle
                        checked={draft.showChinese}
                        onChange={(value) => update("showChinese", value)}
                        label="显示中文解析"
                    />
                </Row>

                <Row label="显示英文解析">
                    <Toggle
                        checked={draft.showEnglish}
                        onChange={(value) => update("showEnglish", value)}
                        label="显示英文解析"
                    />
                </Row>

                <Row label="打乱选项顺序" hint="避免靠位置记答案；关掉则保持题库原始顺序">
                    <Toggle
                        checked={draft.shuffleOptions}
                        onChange={(value) => update("shuffleOptions", value)}
                        label="打乱选项顺序"
                    />
                </Row>
            </Card>

            {draft.bundesland !== "NW" ? (
                <ErrorBanner message="当前题库只包含北威州（NW）的 10 道州题。选择其它州后模拟考将无法出题，直到导入对应州的题目。" />
            ) : null}

            {saveMutation.isError ? <ErrorBanner message="保存失败，请重试" /> : null}

            <div className="flex items-center gap-3">
                <Button onClick={() => saveMutation.mutate(draft)} disabled={!dirty} loading={saveMutation.isPending}>
                    保存设置
                </Button>
                {saved ? (
                    <span className="flex items-center gap-1.5 text-sm text-good">
                        <Check className="size-4" aria-hidden />
                        已保存
                    </span>
                ) : null}
            </div>
        </div>
    );
}
